import { EXEC_LINE_SOURCE_COLUMNS, EXEC_LINE_SOURCE_TABLE } from "./execLineSource.mjs";
import type { ExecLineSourceRow } from "./execLineSource.d.mts";
import { ExecKpiFetchError, fetchAllPages, isTableMissingMessage } from "./execKpiFetch.mjs";
import { isColumnMissingError } from "./sanityGate";
import { getSupabase } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * S1-exec-from-imports: the Executive page's read of public.exec_line_source
 * (migration 0082), the pipeline's per-line source switch.
 *
 * Client readable (tenant policy, like exec_kpi and board_drill), bound to the
 * tenant through scopeTenant (MER-245), and paged past PostgREST's 1,000-row
 * cap with the same fetchAllPages the workbook read uses.
 *
 * Until 0082 is applied the table does not exist. That is not an error the
 * page shows: the read resolves to no rows, and every line keeps the
 * workbook with its "From your workbook" label, exactly the page as it was
 * before the switch. Any other failure throws, and the page does the same
 * thing (it never blocks the workbook on the switch) after a console.warn.
 */

export interface ExecLineSourceFetchResult {
  rows: ExecLineSourceRow[];
  truncated: boolean;
  tableMissing: boolean;
}

function isMissing(message: string): boolean {
  return isTableMissingMessage(message) || isColumnMissingError(message);
}

export async function fetchExecLineSourceRows(
  tenantId: string,
): Promise<ExecLineSourceFetchResult> {
  const supabase = getSupabase();
  try {
    const result = await fetchAllPages<ExecLineSourceRow>(
      (from, to) =>
        scopeTenant(
          supabase.from(EXEC_LINE_SOURCE_TABLE).select(EXEC_LINE_SOURCE_COLUMNS),
          tenantId,
        )
          .order("sheet", { ascending: true })
          .order("client_label", { ascending: true })
          .order("period", { ascending: true })
          .range(from, to)
          // The select list is a runtime string (pinned against 0082 by
          // exec-line-source.test.mjs), so the row type is asserted here.
          .returns<ExecLineSourceRow[]>(),
      { isMissing },
    );
    return { rows: result.rows, truncated: result.truncated, tableMissing: false };
  } catch (err) {
    if (err instanceof ExecKpiFetchError && err.tableMissing) {
      return { rows: [], truncated: false, tableMissing: true };
    }
    throw err;
  }
}
